import React, { useEffect, useState } from "react";
import qs from "qs";
import { Link } from "react-router-dom";
import { URL } from "../../../constants/URL";
import CS from "../../../services/commonService";
import SearchBox from "../../common/searchBox";
import Table from "../../common/table";
import { useGetAllReferrals } from "../../../queryHooks";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import { useStore } from "../../../zustand/store";

import { modules } from "../../../constants/Modules";

//	Pre-defined values
const columnKey = "REFERRALS";

//	Component starts
const Referrals = () => {
	const lastPageData = useStore((state) => state.lastPageData);
	const setLastPageData = useStore((state) => state.setLastPageData);

	//	State values
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(
		lastPageData?.[modules.REFERRALS]?.page || 1
	);
	const [itemsPerPage, setItemsPerPage] = useState(
		lastPageData?.[modules.REFERRALS]?.items_per_page || 10
	);
	const [usersPayload, setUsersPayload] = useState(
		lastPageData?.[modules.REFERRALS] || {
			pagination: true,
			items_per_page: itemsPerPage,
			page: pageNow,
			sort: { name: "DESC" },
		}
	);

	/******************************************************************************************
	@Purpose    :   To get the users list
	*******************************************************************************************/
	const { data: dataReferrals, isLoading: referralsLoading } =
		useGetAllReferrals(qs.stringify(usersPayload));

	useEffect(() => {
		if (dataReferrals && dataReferrals.status === "success") {
			let respData = dataReferrals.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataReferrals]);

	const columns = [
		{
			label: "Referee Joined Date",
			type: "date",
			sort: true,
			path: "referee.created_at",
			alias: "referee_user.created_at",
			aliasFilter: "referee.created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item?.referee?.created_at),
		},
		{
			label: "Referee",
			type: "string",
			path: "referee_profile.first_name",
			alias: "referee_profile.first_name",
			sort: true,
			maxWidth: true,
			content: (item) => (
				<Link
					to={`${URL.onboarderedUsers}/${item?.referee?.user_id}`}
					onClick={() => {
						setLastPageData({ [modules.REFERRALS]: usersPayload });
					}}
				>
					<div className="d-flex align-items-center">
						<div className="flex-auto">
							{CS.renderImageUrl(item?.referee?.profile_image_url)}
						</div>
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">
								{item?.referee?.first_name} {item?.referee?.last_name}
							</span>
						</div>
					</div>
				</Link>
			),
		},
		{
			label: "Referrer",
			type: "string",
			path: "referrer_profile.first_name",
			alias: "referrer_profile.first_name",
			aliasFilter: "referrer_profile.user_id",
			sort: true,
			filterUsers: true,
			content: (item) => (
				<Link
					to={`${URL.onboarderedUsers}/${item?.referrer?.user_id}`}
					onClick={() => {
						setLastPageData({ [modules.REFERRALS]: usersPayload });
					}}
				>
					<div className="d-flex align-items-center">
						<div className="flex-auto">
							{CS.renderImageUrl(item?.referrer?.profile_image_url)}
						</div>
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">
								{item?.referrer?.first_name} {item?.referrer?.last_name}
							</span>
						</div>
					</div>
				</Link>
			),
		},
		{
			label: "Referral Code",
			type: "string",
			path: "referred_code",
			alias: "data_referrals.referral_code",
			content: (item) => (
				<div className="d-flex justify-content-center">
					{item?.referred_code}
				</div>
			),
		},
	];

	return (
		<>
			<div className="grid-content">
				<div className="user-listing-filterOptions mb-2 d-block">
					<div className="row mb-2">
						<div className="col-sm-8 mb-2 position-static">
							<div className="left-buttons d-flex">
								<SearchBox
									columnKey={columnKey}
									searchValue={
										lastPageData?.[modules.REFERRALS]?.search?.username
									}
									search={(value) => {
										setPageNow(1);
										setUsersPayload({
											...usersPayload,
											search: {
												"data_referrals.referral_code":
													!value && value === "" ? undefined : value?.trim(),
												"referee_profile.first_name":
													!value && value === "" ? undefined : value?.trim(),
												"referee_profile.last_name":
													!value && value === "" ? undefined : value?.trim(),
												"referrer_profile.first_name":
													!value && value === "" ? undefined : value?.trim(),
												"referrer_profile.last_name":
													!value && value === "" ? undefined : value?.trim(),
											},
											page: 1,
										});
									}}
								/>
							</div>
						</div>
					</div>
				</div>
				<Table
					columnKey={columnKey}
					columns={columns}
					tableData={tableData}
					payload={usersPayload}
					sortCol={(obj) => {
						setPageNow(1);
						setUsersPayload({
							...usersPayload,
							sort: {
								[obj.alias]:
									!obj.sort && obj.sort === "" ? undefined : obj.sort,
							},
							page: 1,
						});
					}}
					setCurrentPage={(page) => {
						setPageNow(page);
						setUsersPayload({
							...usersPayload,
							page,
						});
					}}
					setCurrentLimit={(items_per_page) => {
						setPageNow(1);
						setItemsPerPage(items_per_page);
						setUsersPayload({
							...usersPayload,
							items_per_page,
							page: 1,
						});
					}}
					loading={referralsLoading}
					filterValue={async (obj) => {
						if (obj.filter === null) {
							const resp = await CS.filterData(usersPayload.filter, obj);
							setPageNow(1);
							setUsersPayload({
								...usersPayload,
								filter: resp,
								page: 1,
							});
						} else {
							setPageNow(1);
							setUsersPayload({
								...usersPayload,
								filter: {
									...usersPayload.filter,
									[obj.alias]: obj.filter,
								},
								page: 1,
							});
						}
					}}
					filterDate={(dates) => {
						setPageNow(1);
						setUsersPayload({
							...usersPayload,
							filter_date: dates && {
								[dates.alias]: String(dates.filter),
							},
							page: 1,
						});
					}}
				/>
			</div>
		</>
	);
};

export default Referrals;
